import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  SkeletonTable,
  Tabs,
  useClientTable,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { money } from '@/lib/report-window';
import {
  queueReasonLabel,
  QUEUE_TAB,
  SALES_ACTION,
  SALES_QUERY_KEY,
  dateLabel,
  daysLabel,
  readSalesQueue,
  runSalesAction,
  salesforceOpportunityUrl,
  type QueueTab,
  type SalesAction,
  type SalesActionResult,
  type SalesQueueRow,
} from '@/lib/sales';

/** URL keys for the tab and the owner filter (search, sort and page use the shared keys). */
const QUEUE_PARAM = { tab: 'tab', owner: 'owner' } as const;

/** Sortable columns, named once. */
const QUEUE_SORT_KEYS = ['name', 'stageName', 'estimate', 'lastTouchAt', 'closeDate', 'idleDays'] as const;

/** Longest idle first. */
const DEFAULT_SORT: DataTableSort = { key: 'idleDays', direction: 'desc' };

/** The close actions, and what each one says. */
type CloseAction = typeof SALES_ACTION.closeLost | typeof SALES_ACTION.closeUnqualified;
const CLOSE_COPY: Readonly<Record<CloseAction, { verb: string; stage: string }>> = {
  [SALES_ACTION.closeLost]: { verb: 'Close as Lost', stage: 'Closed Lost' },
  [SALES_ACTION.closeUnqualified]: { verb: 'Close as Unqualified', stage: 'Closed - Unqualified' },
};

function isTab(value: string | null): value is QueueTab {
  return value === QUEUE_TAB.shouldClose || value === QUEUE_TAB.goingStale;
}

/** "1 opportunity" / "12 opportunities". */
function opportunities(count: number): string {
  return count === 1 ? '1 opportunity' : `${count.toLocaleString('en-US')} opportunities`;
}

/** What an action did, in one line. */
function resultLine(result: SalesActionResult): string {
  const count = opportunities(result.appliedIds.length);
  if (result.action === SALES_ACTION.snooze) return `Snoozed ${count} for 14 days.`;
  const stage = result.action === SALES_ACTION.closeLost ? 'Closed Lost' : 'Closed - Unqualified';
  return `Moved ${count} to ${stage} in Salesforce.`;
}

/**
 * Analytics → Sales → Stale review queue: open opportunities that should
 * already be closed (event date passed, 30 days past the close date, or 60
 * days without a logged email, call or stage change) and ones going stale.
 * Thomas, Cathy and Roy close them as Lost or Unqualified in Salesforce, or
 * snooze them for 14 days, one at a time or in bulk (hq sales D7, D8).
 *
 * @returns The review queue page.
 */
export function SalesStalePage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get(QUEUE_PARAM.tab);
  const tab: QueueTab = isTab(tabParam) ? tabParam : QUEUE_TAB.shouldClose;
  const owner = params.get(QUEUE_PARAM.owner) ?? '';
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [pendingClose, setPendingClose] = useState<{ action: CloseAction; ids: readonly string[] } | null>(null);
  const [lossReason, setLossReason] = useState('');
  const [result, setResult] = useState<SalesActionResult | null>(null);

  const queue = useQuery({
    queryKey: SALES_QUERY_KEY.queue(owner || undefined),
    queryFn: ({ signal }) => readSalesQueue(owner || undefined, signal),
    placeholderData: keepPreviousData,
  });

  const action = useMutation({
    mutationFn: (input: { action: SalesAction; ids: readonly string[]; lossReason?: string }) =>
      runSalesAction({
        action: input.action,
        opportunityIds: input.ids,
        ...(input.lossReason ? { lossReason: input.lossReason } : {}),
      }),
    onSuccess: async (outcome) => {
      setResult(outcome);
      setSelected(new Set());
      setPendingClose(null);
      setLossReason('');
      await queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY.root });
    },
  });

  const rows = (tab === QUEUE_TAB.shouldClose ? queue.data?.shouldClose : queue.data?.goingStale) ?? [];
  const table = useClientTable<SalesQueueRow>({
    rows,
    searchText: (row) => [row.name, row.accountName, row.ownerName, row.stageName, row.lastTouchSubject].join(' '),
    sortKeys: QUEUE_SORT_KEYS,
    defaultSort: DEFAULT_SORT,
  });

  function setParam(key: string, value: string | null): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
    setSelected(new Set());
  }

  function toggle(id: string): void {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const pageIds = table.pageRows.map((row) => row.opportunityId);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  function togglePage(): void {
    setSelected((previous) => {
      const next = new Set(previous);
      for (const id of pageIds) {
        if (allOnPage) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  const selectedIds = [...selected];
  const roster = new Set(queue.data?.roster ?? []);
  const owners = [...(queue.data?.owners ?? [])].sort(
    (a, b) => Number(roster.has(b.ownerName)) - Number(roster.has(a.ownerName)) || a.ownerName.localeCompare(b.ownerName),
  );
  const shouldCloseCount = queue.data?.shouldClose.length ?? 0;
  const goingStaleCount = queue.data?.goingStale.length ?? 0;
  const closeCopy = pendingClose ? CLOSE_COPY[pendingClose.action] : null;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/analytics/sales">Sales</BackLink>
      <PageHeading
        eyebrow="SALES"
        title="Stale review queue"
        subtitle="Open opportunities that should already be closed, and ones going stale. Close them in Salesforce or snooze them for 14 days."
      />

      <Tabs<QueueTab>
        label="Review queue"
        active={tab}
        onChange={(next) => setParam(QUEUE_PARAM.tab, next === QUEUE_TAB.shouldClose ? null : next)}
        items={[
          { key: QUEUE_TAB.shouldClose, label: 'Should be closed', count: shouldCloseCount },
          { key: QUEUE_TAB.goingStale, label: 'Going stale', count: goingStaleCount },
        ]}
      />

      <div className="mt-4 mb-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-portal-small text-portal-ink">
          Owner
          <SelectInput
            density="compact"
            aria-label="Filter by owner"
            value={owner}
            onChange={(event) => setParam(QUEUE_PARAM.owner, event.target.value || null)}
            className="min-w-52"
          >
            <option value="">All owners</option>
            {owners.map((entry) => (
              <option key={entry.ownerId} value={entry.ownerId}>
                {entry.ownerName || 'No owner'}
              </option>
            ))}
          </SelectInput>
        </label>
        <SearchInput
          label="Search the queue"
          placeholder="Opportunity, account, stage or last subject"
          value={table.search}
          onChange={(event) => table.setSearch(event.target.value)}
          className="w-full max-w-96 min-w-56"
        />
      </div>

      {result ? (
        <div className="mb-3 space-y-2">
          <Panel className="flex items-center justify-between gap-3 p-4 text-portal-small text-portal-ink">
            <span>{resultLine(result)}</span>
            <Button variant="ghost" size="compact" onClick={() => setResult(null)}>
              Dismiss
            </Button>
          </Panel>
          {result.failures.length > 0 ? (
            <ErrorNotice
              inline
              tone="warning"
              message={`${opportunities(result.failures.length)} not changed: ${result.failures
                .map((failure) => `${failure.opportunityId} (${failure.reason})`)
                .join('; ')}`}
            />
          ) : null}
        </div>
      ) : null}
      {action.isError ? (
        <div className="mb-3">
          <ErrorNotice inline message={errorMessage(action.error)} />
        </div>
      ) : null}

      {queue.isError ? (
        <ErrorNotice title="Review queue unavailable" message={errorMessage(queue.error)} />
      ) : queue.isPending ? (
        <SkeletonTable rows={10} />
      ) : table.matched === 0 ? (
        <Panel>
          <EmptyState
            title={table.search || owner ? 'Nothing matches' : tab === QUEUE_TAB.shouldClose ? 'Nothing to close' : 'Nothing going stale'}
            message={
              table.search || owner
                ? 'Try a different search or owner.'
                : 'Every open opportunity has been worked recently. Snoozed ones come back after 14 days.'
            }
          />
        </Panel>
      ) : (
        <>
          <Panel className="mb-3 flex flex-wrap items-center gap-3 p-3">
            <label className="flex items-center gap-2 text-portal-small text-portal-ink">
              <input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label="Select every opportunity on this page" />
              {selectedIds.length > 0 ? `${opportunities(selectedIds.length)} selected` : 'Select all on this page'}
            </label>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button
                size="compact"
                variant="secondary"
                disabled={selectedIds.length === 0 || action.isPending}
                onClick={() => setPendingClose({ action: SALES_ACTION.closeLost, ids: selectedIds })}
              >
                Close as Lost
              </Button>
              <Button
                size="compact"
                variant="secondary"
                disabled={selectedIds.length === 0 || action.isPending}
                onClick={() => setPendingClose({ action: SALES_ACTION.closeUnqualified, ids: selectedIds })}
              >
                Close as Unqualified
              </Button>
              <Button
                size="compact"
                variant="secondary"
                disabled={selectedIds.length === 0 || action.isPending}
                onClick={() => action.mutate({ action: SALES_ACTION.snooze, ids: selectedIds })}
              >
                Snooze 14 days
              </Button>
            </div>
          </Panel>

          <DataTable
            headers={[
              'Select',
              { label: 'Opportunity', sortKey: 'name' },
              { label: 'Stage', sortKey: 'stageName' },
              'Why',
              { label: 'Estimate', sortKey: 'estimate' },
              { label: 'Last email or call', sortKey: 'lastTouchAt' },
              { label: 'Close date', sortKey: 'closeDate' },
              { label: 'Idle', sortKey: 'idleDays' },
              'Actions',
            ]}
            visuallyHiddenHeaders={[0, 8]}
            rightAlignedHeaders={[4, 7]}
            sort={table.sort}
            onSort={table.onSort}
            minWidth={1080}
          >
            {table.pageRows.map((row) => (
              <tr key={row.opportunityId} className="border-t border-portal-line align-middle">
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selected.has(row.opportunityId)}
                    onChange={() => toggle(row.opportunityId)}
                    aria-label={`Select ${row.name || row.opportunityId}`}
                  />
                </td>
                <td className="min-w-72 p-3 text-portal-small">
                  <span className="block font-bold text-portal-ink">{row.name || row.opportunityId}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {[row.accountName || 'No account', row.ownerName || 'No owner'].join(' · ')}
                  </span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{row.stageName}</td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  <Pill tone={tab === QUEUE_TAB.shouldClose ? 'danger' : 'warning'}>{queueReasonLabel(row.reason, tab)}</Pill>
                  {row.eventDate && tab === QUEUE_TAB.shouldClose ? (
                    <span className="mt-1 block text-portal-micro text-portal-muted">Event {dateLabel(row.eventDate)}</span>
                  ) : null}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                  {row.estimate === null ? (
                    <span className="text-portal-muted">{row.flagged ? 'Flagged' : 'Not priced'}</span>
                  ) : (
                    money.format(row.estimate)
                  )}
                </td>
                <td className="p-3 text-portal-small">
                  {row.lastTouchAt ? (
                    <>
                      <span className="block max-w-52 truncate text-portal-ink" title={row.lastTouchSubject}>
                        {row.lastTouchSubject || row.lastTouchSubtype}
                      </span>
                      <span className="block text-portal-micro text-portal-muted">
                        {row.lastTouchSubtype} · {dateLabel(row.lastTouchAt)}
                      </span>
                    </>
                  ) : (
                    <span className="text-portal-muted">None logged</span>
                  )}
                </td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{dateLabel(row.closeDate)}</td>
                <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">{daysLabel(row.idleDays)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                    <Button
                      size="compact"
                      variant="ghost"
                      disabled={action.isPending}
                      onClick={() => setPendingClose({ action: SALES_ACTION.closeLost, ids: [row.opportunityId] })}
                    >
                      Close
                    </Button>
                    <Button
                      size="compact"
                      variant="ghost"
                      disabled={action.isPending}
                      onClick={() => action.mutate({ action: SALES_ACTION.snooze, ids: [row.opportunityId] })}
                    >
                      Snooze
                    </Button>
                    <a
                      href={salesforceOpportunityUrl(row.opportunityId)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-brand"
                      aria-label={`Open ${row.name || row.opportunityId} in Salesforce`}
                    >
                      <ExternalLinkIcon className="size-4" aria-hidden="true" />
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={table.page}
            totalPages={table.totalPages}
            onChange={table.setPage}
            summary={table.summary}
            className="mt-3"
          />
          <p className="mt-2 text-portal-micro text-portal-muted">
            Should be closed: the event date passed, the close date is more than 30 days gone, or 60 days passed with
            no logged email, call or stage change. Going stale: 30 days with none of those, or past the close date.
            Signed - Postponed never appears.{' '}
            {queue.data && queue.data.snoozed > 0 ? `${opportunities(queue.data.snoozed)} snoozed.` : ''}
          </p>
        </>
      )}

      <ConfirmDialog
        open={pendingClose !== null}
        title={
          pendingClose && closeCopy
            ? `${closeCopy.verb}: ${opportunities(pendingClose.ids.length)}?`
            : 'Close opportunities?'
        }
        message={
          <div className="space-y-3">
            <p>
              {pendingClose && closeCopy
                ? `This sets the stage to ${closeCopy.stage} in Salesforce${pendingClose.ids.length > 1 ? ' for every selected opportunity' : ''}. Reopening means changing the stage back in Salesforce.`
                : ''}
            </p>
            {pendingClose ? (
              <label className="block text-portal-small text-portal-ink">
                Close as
                <SelectInput
                  className="mt-1 w-full"
                  aria-label="Close as"
                  value={pendingClose.action}
                  onChange={(event) =>
                    setPendingClose({
                      ...pendingClose,
                      action:
                        event.target.value === SALES_ACTION.closeUnqualified
                          ? SALES_ACTION.closeUnqualified
                          : SALES_ACTION.closeLost,
                    })
                  }
                >
                  <option value={SALES_ACTION.closeLost}>Lost</option>
                  <option value={SALES_ACTION.closeUnqualified}>Unqualified</option>
                </SelectInput>
              </label>
            ) : null}
            {pendingClose?.action === SALES_ACTION.closeLost ? (
              <label className="block text-portal-small text-portal-ink">
                Loss reason <span className="text-rose-600">*</span>
                <SelectInput
                  className="mt-1 w-full"
                  aria-label="Loss reason"
                  value={lossReason}
                  onChange={(event) => setLossReason(event.target.value)}
                >
                  <option value="">Choose a reason</option>
                  {(queue.data?.lossReasons ?? []).map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </SelectInput>
              </label>
            ) : null}
          </div>
        }
        confirmLabel={closeCopy?.verb ?? 'Close'}
        tone="danger"
        pending={action.isPending}
        confirmDisabled={pendingClose?.action === SALES_ACTION.closeLost && lossReason === ''}
        error={action.isError ? errorMessage(action.error) : undefined}
        onCancel={() => {
          setPendingClose(null);
          action.reset();
        }}
        onConfirm={() => {
          if (!pendingClose) return;
          action.mutate({
            action: pendingClose.action,
            ids: pendingClose.ids,
            ...(pendingClose.action === SALES_ACTION.closeLost ? { lossReason } : {}),
          });
        }}
      />
    </section>
  );
}
